import {
  AfterContentInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { translationProvider } from './i18n/translation.provider';
import { DatepickerInterval, DatepickerMonth, DatepickerPeriod } from '../../datepicker-range-advanced.interfaces';
import { PillComponent } from '../../../pill/pill.component';
import { Observable, Subject, takeUntil } from 'rxjs';
import { getDatepickerMonthPeriods, getLastDayOfMonth, getSelectionInterval } from '../../utils/calendar-month';

@Component({
  selector: 'rx-calendar-months',
  standalone: true,
  imports: [CommonModule, PillComponent, TranslocoModule],
  templateUrl: './calendar-months.component.html',
  styleUrl: './calendar-months.component.scss',
  providers: [translationProvider],
})
export class CalendarMonthsComponent implements OnInit, AfterContentInit, OnDestroy {
  @Input() minDate: Date = new Date();
  @Input() maxDate: Date = new Date();
  @Input() testIdPrefix: string;
  @Input() resetTabSelections$: Observable<boolean>;

  @Output() selectionChange: EventEmitter<DatepickerInterval> = new EventEmitter<DatepickerInterval>();
  datepickerPeriods: DatepickerPeriod[] = [];
  referenceRangeMonth: DatepickerMonth;

  destroyed$ = new Subject();


  ngOnInit(): void {
    this.datepickerPeriods = getDatepickerMonthPeriods(this.minDate, this.maxDate);
  }

  ngAfterContentInit(): void {
    this.resetTabSelections$.pipe(takeUntil(this.destroyed$)).subscribe((): void => {
      this.resetSelections();
      this.datepickerPeriods = getDatepickerMonthPeriods(this.minDate, this.maxDate);
    });
  }

  onMonthClick(clickedMonth: DatepickerMonth): void {
    const selectedMonths: string[] = this.getSelectedMonthIds();
    let selectedDates: DatepickerInterval;

    if (this.isSecondSelection(selectedMonths, clickedMonth)) {
      this.selectMonthsBetweenDates(clickedMonth.id, selectedMonths[0]);
      selectedDates = getSelectionInterval(clickedMonth.id, selectedMonths[0], this.minDate, this.maxDate);
      this.referenceRangeMonth = undefined;
    } else {
      this.selectSingleMonth(clickedMonth.id);
      selectedDates = getSelectionInterval(clickedMonth.id, clickedMonth.id, this.minDate, this.maxDate);
      this.referenceRangeMonth = clickedMonth;
    }
    this.selectionChange.emit(selectedDates);
  }

  getSelectedMonthIds(): string[] {
    return this.datepickerPeriods.flatMap((year) => year.months.filter((month) => month.selected).map((month) => month.id));
  }


  resetSelections(): void {
    this.referenceRangeMonth = undefined;
    this.datepickerPeriods.forEach((year) =>
      year.months.forEach((month) => {
        month.selected = false;
        month.inRange = false;
      })
    );
  }

  selectSingleMonth(monthId: string): void {
    this.datepickerPeriods.forEach((year) =>
      year.months.forEach((month) => {
        month.selected = month.id === monthId;
        month.inRange = false;
      })
    );
  }

  selectMonthsBetweenDates(startYearMonth: string, endYearMonth: string): void {
    if (new Date(startYearMonth) > new Date(endYearMonth)) {
      [startYearMonth, endYearMonth] = [endYearMonth, startYearMonth];
    }

    const startDate: Date = new Date(`${startYearMonth}`);
    const endDate: Date = new Date(`${endYearMonth}-${getLastDayOfMonth(endYearMonth)}`);

    for (const year of this.datepickerPeriods) {
      for (const month of year.months) {
        const currentDate = new Date(`${month.id}`);

        if (currentDate >= startDate && currentDate <= endDate) {
          month.selected = true;
        }
      }
    }
  }

  private isSecondSelection(selectedMonths: string[], selectedDatepickerMonth: DatepickerMonth): boolean {
    return selectedMonths.length === 1 && selectedDatepickerMonth.id !== selectedMonths[0];
  }

  onMouseHover(hoveredDatepickerMonth: DatepickerMonth): void {
    if (this.referenceRangeMonth) {
      let startYearMonth: string = hoveredDatepickerMonth.id;
      let endYearMonth: string = this.referenceRangeMonth.id;

      if (new Date(startYearMonth) > new Date(endYearMonth)) {
        [startYearMonth, endYearMonth] = [endYearMonth, startYearMonth];
      }

      const startDate: Date = new Date(`${startYearMonth}`);
      const endDate: Date = new Date(`${endYearMonth}-${getLastDayOfMonth(endYearMonth)}`);

      for (const year of this.datepickerPeriods) {
        for (const month of year.months) {
          const currentDate = new Date(`${month.id}`);
          month.inRange = currentDate >= startDate && currentDate <= endDate;
        }
      }
    }
  }

  onMouseOut(): void {
    this.resetHoverSelection();
  }

  private resetHoverSelection(): void {
    this.datepickerPeriods.forEach((year) =>
      year.months.forEach((month) => {
        month.inRange = false;
      })
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
